import React, { useEffect, useState } from "react";
import { useAuth } from "../../store/hooks/useAuth";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { RoleTypeEnum } from "../../utils/constants";
import OrganizationSkeleton from "./OrganizationSkeleton";
import {
  useGetWorkingHoursConfigQuery,
  useUpdateWorkingHoursConfigMutation,
} from "../../store/apis/organization.api";
import { Button } from "../common";
import {
  Clock,
  HelpCircle,
  Save,
  RotateCcw,
  AlertCircle,
  Sparkles,
  CheckCircle2,
  XCircle
} from "lucide-react";
import toast from "react-hot-toast";

// Helper math functions for SVG Gauge
const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (centerX: number, centerY: number, radius: number, startAngle: number, endAngle: number) => {
  if (Math.abs(endAngle - startAngle) < 0.1) return "";
  const start = polarToCartesian(centerX, centerY, radius, Math.min(359.99, endAngle));
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
};

const OrganizationConfig: React.FC = () => {
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const isAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.ADMIN.toLowerCase() ||
    user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN.toLowerCase();
  const canManage = isAdmin || useHasPermission(PERMISSIONS.ORGANIZATION_CONFIG_MANAGE);

  // Queries & Mutations
  const {
    data: configData,
    isLoading: isFetching,
    refetch,
  } = useGetWorkingHoursConfigQuery(organizationId || "", {
    skip: !organizationId,
  });

  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateWorkingHoursConfigMutation();

  // Local state for interactive editing
  const [fullDay, setFullDay] = useState<number>(7);
  const [halfDay, setHalfDay] = useState<number>(4);

  // Simulator state
  const [simulatedHours, setSimulatedHours] = useState<number>(6);

  // Populate local state when query completes
  useEffect(() => {
    if (configData) {
      setFullDay(configData.fullDayHours);
      setHalfDay(configData.halfDayHours);
    }
  }, [configData]);

  if (!organizationId) {
    return (
      <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-500">
        No organization associated with your account.
      </div>
    );
  }

  if (isFetching) {
    return <OrganizationSkeleton />;
  }

  const handleSave = async () => {
    try {
      await updateConfig({
        orgId: organizationId,
        body: {
          fullDayHours: fullDay,
          halfDayHours: halfDay,
        },
      }).unwrap();
      toast.success("Working hours threshold configured successfully!");
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update configurations.");
    }
  };

  const handleReset = () => {
    setFullDay(7);
    setHalfDay(4);
    toast.success("Reset to system defaults (7h / 4h). Save to apply.");
  };

  const handleApplyPreset = (hDay: number, fDay: number) => {
    setHalfDay(hDay);
    setFullDay(fDay);
    toast.success(`Preset applied (${fDay}h Full / ${hDay}h Half). Save to apply.`);
  };

  // Percentage calculations for visual timeline track
  const halfDayPct = (halfDay / 12) * 100;
  const fullDayPct = (fullDay / 12) * 100;

  // Spacing gaps for visual disconnection of the 3 colors
  const gap = 4.5; // degrees gap between segments
  const angle1 = (halfDay / 12) * 360;
  const angle2 = (fullDay / 12) * 360;

  const s1Start = gap;
  const s1End = Math.max(s1Start + 1, angle1 - gap);

  const s2Start = Math.min(angle2 - gap - 1, angle1 + gap);
  const s2End = Math.max(s2Start + 1, angle2 - gap);

  const s3Start = Math.min(360 - gap - 1, angle2 + gap);
  const s3End = Math.max(s3Start + 1, 360 - gap);

  // Simulator values
  let simulatedStatus = "Present";
  let simulatedColor = "emerald";
  let simulatedDescription = "Marked as Present";
  let simulatedMessage = "Employee receives full day attendance credit.";

  if (simulatedHours < halfDay) {
    simulatedStatus = "Absent";
    simulatedColor = "rose";
    simulatedDescription = "Marked as Absent";
    simulatedMessage = `Shift duration is below the half-day threshold (${halfDay} hrs).`;
  } else if (simulatedHours < fullDay) {
    simulatedStatus = "Half Day";
    simulatedColor = "amber";
    simulatedDescription = "Marked as Half Day";
    simulatedMessage = `Meets half-day threshold but is below full-day (${fullDay} hrs).`;
  }

  return (
    <div className="space-y-6 py-6 px-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2 md:text-3xl">
            Organization Configuration
          </h1>
        </div>
      </div>

      {/* Tabs Selector Bar */}
      <div className="flex flex-wrap items-center gap-3 !mt-2 !mb-5">
        <div className="bg-white border border-slate-200 rounded-lg md:rounded-full shadow-soft px-1.5 py-1.5 flex flex-wrap gap-1">
          <button
            className="px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-colors duration-200 bg-primary-600 text-white shadow"
          >
            Working Hours
          </button>
        </div>
      </div>

      {/* Modern Split Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Form Settings and Preset Configurations (7 cols) */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl shadow-soft p-6 md:p-8 space-y-8">
          
          {/* Section Title */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="p-1.5 bg-primary-50 text-primary-600 rounded-lg">
                <Clock className="w-4 h-4" />
              </span>
              <h2 className="text-lg font-bold text-slate-805 text-slate-800 mb-0">
                Working Hours Thresholds
              </h2>
            </div>
            <p className="text-sm text-slate-500">
              Configure minimum daily worked hours requirements. Threshold boundaries dynamically calculate shifts for payroll and attendance status.
            </p>
          </div>

          {/* Quick Presets Selection */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block">
              Quick Shift Presets
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                disabled={!canManage}
                onClick={() => handleApplyPreset(4, 8)}
                className={`p-3 text-left rounded-xl border transition-all duration-200 text-sm font-medium ${
                  halfDay === 4 && fullDay === 8
                    ? "bg-primary-50 border-primary-300 text-primary-700 shadow-sm"
                    : "border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50"
                }`}
              >
                <div className="font-bold text-xs uppercase text-slate-400 mb-1">Standard</div>
                <div className="text-slate-855 font-bold text-slate-800">8.0h Shift</div>
                <div className="text-xs text-slate-500 mt-1">4.0h half-day limit</div>
              </button>

              <button
                type="button"
                disabled={!canManage}
                onClick={() => handleApplyPreset(4.5, 9)}
                className={`p-3 text-left rounded-xl border transition-all duration-200 text-sm font-medium ${
                  halfDay === 4.5 && fullDay === 9
                    ? "bg-primary-50 border-primary-300 text-primary-700 shadow-sm"
                    : "border-slate-200 hover:border-slate-300 bg-slate-50/50 hover:bg-slate-50"
                }`}
              >
                <div className="font-bold text-xs uppercase text-slate-400 mb-1">Flexible</div>
                <div className="text-slate-855 font-bold text-slate-800">9.0h Shift</div>
                <div className="text-xs text-slate-500 mt-1">4.5h half-day limit</div>
              </button>

              <button
                type="button"
                disabled={true}
                className={`p-3 text-left rounded-xl border text-sm transition-all duration-200 ${
                  (halfDay !== 4 || fullDay !== 8) && (halfDay !== 4.5 || fullDay !== 9)
                    ? "bg-violet-50 border-violet-300 text-violet-755 font-medium"
                    : "border-dashed border-slate-200 bg-transparent text-slate-400 opacity-60"
                }`}
              >
                <div className="font-bold text-xs uppercase text-slate-400 mb-1">Custom</div>
                <div className="text-slate-855 font-bold text-slate-800">{fullDay.toFixed(1)}h Shift</div>
                <div className="text-xs text-slate-500 mt-1">{halfDay.toFixed(1)}h half-day limit</div>
              </button>
            </div>
          </div>

          {/* Form Sliders */}
          <div className="space-y-6">
            {/* Half Day Slider */}
            <div className="p-5 border border-slate-100 rounded-xl bg-slate-50/30 hover:bg-slate-50/70 transition-all duration-200">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                  Half Day Threshold
                  <span title="Shift duration below this will be counted as Absent.">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </span>
                </label>
                <div className="flex items-center gap-1.5 bg-white border border-slate-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 px-2.5 py-1 rounded-lg shadow-sm transition-all duration-150">
                  <input
                    type="number"
                    disabled={!canManage}
                    step={0.1}
                    min={1}
                    max={fullDay - 0.1}
                    value={halfDay}
                    onChange={(e) => {
                      const val = Math.max(1, parseFloat(e.target.value) || 0);
                      setHalfDay(Math.min(val, fullDay - 0.1));
                    }}
                    className="w-12 text-right font-mono text-sm text-slate-855 focus:outline-none font-semibold"
                  />
                  <span className="text-xs text-slate-400 font-semibold select-none">hrs</span>
                </div>
              </div>

              <input
                type="range"
                disabled={!canManage}
                min={1}
                max={12}
                step={0.5}
                value={halfDay}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setHalfDay(Math.min(val, fullDay - 0.5));
                }}
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600 focus:outline-none"
              />

              <div className="relative h-4 text-[10px] text-slate-400 font-semibold mt-1.5 px-1">
                <span className="absolute left-0">1.0h</span>
                <span className="absolute left-[27.27%] transform -translate-x-1/2 whitespace-nowrap">4.0h (Default)</span>
                <span className="absolute left-[63.63%] transform -translate-x-1/2">8.0h</span>
                <span className="absolute right-0">12.0h</span>
              </div>
            </div>

            {/* Full Day Slider */}
            <div className="p-5 border border-slate-100 rounded-xl bg-slate-50/30 hover:bg-slate-50/70 transition-all duration-200">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                  Full Day Threshold
                  <span title="Shift duration above this is marked as Present. Below is Half Day.">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </span>
                </label>
                <div className="flex items-center gap-1.5 bg-white border border-slate-200 focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 px-2.5 py-1 rounded-lg shadow-sm transition-all duration-150">
                  <input
                    type="number"
                    disabled={!canManage}
                    step={0.1}
                    min={halfDay + 0.1}
                    max={24}
                    value={fullDay}
                    onChange={(e) => {
                      const val = Math.max(1, parseFloat(e.target.value) || 0);
                      setFullDay(Math.max(val, halfDay + 0.1));
                    }}
                    className="w-12 text-right font-mono text-sm text-slate-855 focus:outline-none font-semibold"
                  />
                  <span className="text-xs text-slate-400 font-semibold select-none">hrs</span>
                </div>
              </div>

              <input
                type="range"
                disabled={!canManage}
                min={1}
                max={12}
                step={0.5}
                value={fullDay}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setFullDay(Math.max(val, halfDay + 0.5));
                }}
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-violet-600 focus:outline-none"
              />

              <div className="relative h-4 text-[10px] text-slate-400 font-semibold mt-1.5 px-1">
                <span className="absolute left-0">1.0h</span>
                <span className="absolute left-[54.54%] transform -translate-x-1/2 whitespace-nowrap">7.0h (Default)</span>
                <span className="absolute left-[72.72%] transform -translate-x-1/2">9.0h</span>
                <span className="absolute right-0">12.0h</span>
              </div>
            </div>
          </div>

          {/* Action Panel */}
          {canManage && (
            <div className="flex justify-end items-center gap-3 pt-6 border-t border-slate-100">
              <Button
                appearance="secondary"
                icon={<RotateCcw className="w-4 h-4" />}
                onClick={handleReset}
                disabled={isUpdating}
              >
                Reset to Defaults
              </Button>
              <Button
                appearance="primary"
                icon={<Save className="w-4 h-4" />}
                onClick={handleSave}
                loading={isUpdating}
                disabled={isUpdating}
              >
                Save Configurations
              </Button>
            </div>
          )}
        </div>

        {/* Right Column: Circular Shift Gauge Visualizer & Simulator (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Interactive Simulator Card */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-soft p-6 flex flex-col items-center justify-center space-y-6">
            <div className="w-full text-left">
              <div className="flex items-center gap-2 mb-1">
                <span className="p-1.5 bg-violet-50 text-violet-600 rounded-lg">
                  <Sparkles className="w-4 h-4 animate-pulse animate-duration-1000" />
                </span>
                <h2 className="text-lg font-bold text-slate-855 text-slate-800 mb-0">
                  Rule Simulator
                </h2>
              </div>
              <p className="text-xs text-slate-500">
                Drag the shift slider below to simulate Worked Hours and instantly see how the daily attendance evaluates.
              </p>
            </div>

            {/* Circular Gauge Centerpiece */}
            <div className="relative w-56 h-56 flex items-center justify-center">
              
              {/* Dynamic SVG Drawing */}
              <svg className="absolute inset-0 w-full h-full transform -rotate-90 pointer-events-none" viewBox="0 0 200 200">
                {/* Background Ring */}
                <circle cx="100" cy="100" r="75" stroke="#e2e8f0" strokeWidth="2" fill="transparent" />

                {/* Absent Arc Sector (Red) */}
                <path
                  d={describeArc(100, 100, 75, s1Start, s1End)}
                  stroke="#f43f5e"
                  strokeWidth="8"
                  fill="transparent"
                  strokeLinecap="round"
                />

                {/* Half Day Arc Sector (Amber) */}
                <path
                  d={describeArc(100, 100, 75, s2Start, s2End)}
                  stroke="#f59e0b"
                  strokeWidth="8"
                  fill="transparent"
                  strokeLinecap="round"
                />

                {/* Present Arc Sector (Emerald) */}
                <path
                  d={describeArc(100, 100, 75, s3Start, s3End)}
                  stroke="#10b981"
                  strokeWidth="8"
                  fill="transparent"
                  strokeLinecap="round"
                />

                {/* Simulated worked hours point handle */}
                {(() => {
                  const angle = (simulatedHours / 12) * 360;
                  const coords = polarToCartesian(100, 100, 75, angle);
                  return (
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r="7.5"
                      className="fill-slate-800 stroke-white stroke-2 shadow-lg"
                    />
                  );
                })()}
              </svg>

              {/* Gauge Center text content */}
              <div className="text-center space-y-1 select-none z-10">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Simulated</span>
                <div className="text-3xl font-black text-slate-800 leading-none tracking-tight font-mono">
                  {simulatedHours.toFixed(1)}h
                </div>
                <div className={`inline-flex px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider shadow-sm mt-2 transition-all duration-300 ${
                  simulatedColor === "emerald"
                    ? "bg-emerald-600 text-white"
                    : simulatedColor === "amber"
                    ? "bg-amber-500 text-white"
                    : "bg-rose-500 text-white"
                }`}>
                  {simulatedStatus}
                </div>
              </div>
            </div>

            {/* Slider to interact with simulator */}
            <div className="w-full space-y-2 px-2">
              <div className="flex justify-between items-center text-xs font-semibold text-slate-655">
                <span>Drag to Test Hours</span>
                <span className="font-mono text-slate-400">Scale: 0h - 12h</span>
              </div>
              <input
                type="range"
                min={0}
                max={12}
                step={0.1}
                value={simulatedHours}
                onChange={(e) => setSimulatedHours(parseFloat(e.target.value))}
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600 focus:outline-none"
              />
            </div>

            {/* Threshold references indicators */}
            <div className="w-full grid grid-cols-3 gap-2 text-[10px] font-bold text-center">
              <div className="bg-rose-50/50 text-rose-700 p-2.5 rounded-xl border border-rose-200/40">
                <div className="uppercase tracking-wide opacity-80 mb-0.5">Absent Limit</div>
                <div className="font-mono text-xs font-black">&lt; {halfDay.toFixed(1)}h</div>
              </div>
              <div className="bg-amber-50/50 text-amber-700 p-2.5 rounded-xl border border-amber-200/40">
                <div className="uppercase tracking-wide opacity-80 mb-0.5">Half Day Zone</div>
                <div className="font-mono text-xs font-black">{halfDay.toFixed(1)}h - {fullDay.toFixed(1)}h</div>
              </div>
              <div className="bg-emerald-50/50 text-emerald-700 p-2.5 rounded-xl border border-emerald-200/40">
                <div className="uppercase tracking-wide opacity-80 mb-0.5">Present Zone</div>
                <div className="font-mono text-xs font-black">&ge; {fullDay.toFixed(1)}h</div>
              </div>
            </div>

          </div>

          {/* Quick Informational card */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex gap-3">
            <AlertCircle className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                How attendance is processed
              </h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                When employees check-out or request attendance regularization, the system tallies total active work hours (excluding breaks) against these thresholds to assign attendance records.
              </p>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default OrganizationConfig;
